import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { Search, Filter, Edit2, Trash2, X, MapPin, Star, Settings, Map, History, Images, PlusCircle } from 'lucide-react';
import EditLocationPanel from './EditLocationPanel';
import WelcomePanelEditor from './admin/WelcomePanelEditor';
import WelcomePanelHistory from './admin/WelcomePanelHistory';
import GalleryAdminPanel from './admin/GalleryAdminPanel';

// Função para calcular a pontuação
const calcularPontuacao = (location) => {
  let pontuacao = 0;
  
  // Título (15 pontos)
  if (location.titulo && location.titulo !== "Título não disponível") {
    pontuacao += 15;
  }
  
  // Descrição detalhada (25 pontos)
  if (location.descricao_detalhada && location.descricao_detalhada.length > 100) {
    pontuacao += 25;
  }
  
  // Imagens (15 pontos)
  if (location.imagens && location.imagens.length > 0) {
    pontuacao += 15;
  }
  
  // Áudio (15 pontos)
  if (location.audio) {
    pontuacao += 15;
  }
  
  // Links (15 pontos)
  if (location.links && location.links.length > 0) {
    pontuacao += 15;
  }

  // Vídeo (15 pontos)
  if (location.video) {
    pontuacao += 15;
  }

  return Math.round((pontuacao / 100) * 100);
};

const AdminPanel = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('todos');
  const [error, setError] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [editingLocation, setEditingLocation] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('locations');

  // Garantir que a rolagem esteja habilitada no painel admin
  useEffect(() => {
    // Forçar overflow auto no body para o painel admin
    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    
    return () => {
      // Restaurar o estado original quando sair do componente
      document.body.style.overflow = '';
      document.body.style.height = '';
    };
  }, []);

  // Monitorar mudanças no overflow do body e corrigir se necessário
  useEffect(() => {
    const checkOverflow = () => {
      if (document.body.style.overflow === 'hidden') {
        document.body.style.overflow = 'auto';
      }
    };

    // Verificar a cada 100ms se o overflow foi alterado
    const interval = setInterval(checkOverflow, 100);
    
    return () => clearInterval(interval);
  }, []);

  // Função para buscar todos os locais
  const fetchLocations = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('locations3')
        .select('*')
        .order('titulo', { ascending: true });

      if (error) throw error;
      setLocations(data);
      setError(null);
    } catch (err) {
      console.error('Erro ao buscar locais:', err);
      setError('Não foi possível carregar os locais. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Buscar todos os locais ao carregar o componente
  useEffect(() => {
    fetchLocations();
  }, []);

  // Filtrar locais baseado na busca e tipo selecionado
  const filteredLocations = locations.filter(location => {
    const matchesSearch = location.titulo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         location.descricao_detalhada?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === 'todos' || location.tipo === selectedType;
    return matchesSearch && matchesType;
  });

  // Tipos únicos para o filtro
  const uniqueTypes = ['todos', ...new Set(locations.map(loc => loc.tipo))];

  const handleDelete = async (location) => {
    if (window.confirm(`Tem certeza que deseja excluir o local "${location.titulo}"?`)) {
      try {
        setLoading(true);
        const { error } = await supabase
          .from('locations3')
          .delete()
          .eq('id', location.id);

        if (error) throw error;

        // Atualiza a lista local removendo o item excluído
        setLocations(locations.filter(loc => loc.id !== location.id));
        setError(null);
      } catch (err) {
        console.error('Erro ao excluir local:', err);
        setError('Não foi possível excluir o local. Por favor, tente novamente.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (location) => {
    setEditingLocation(location);
    setIsPanelOpen(true);
  };

  const handleCreateNew = () => {
    setEditingLocation(null); // null indica modo criar
    setIsPanelOpen(true);
  };

  const handleClosePanel = () => {
    setEditingLocation(null);
    setIsPanelOpen(false);
  };

  const handleSaveEdit = async (updatedLocation) => {
    if (!updatedLocation) {
      setError('Dados do local inválidos. Por favor, tente novamente.');
      return;
    }

    const isCreateMode = !updatedLocation.id;

    try {
      setIsSaving(true);
      setError(null);

      const locationData = {
        titulo: updatedLocation.titulo,
        tipo: updatedLocation.tipo,
        descricao_detalhada: updatedLocation.descricao_detalhada,
        localizacao: `${updatedLocation.latitude},${updatedLocation.longitude}`,
        links: updatedLocation.links || null,
        audio: updatedLocation.audio || null,
        imagens: updatedLocation.imagens || null,
      };

      if (isCreateMode) {
        // Criar novo local
        const { data, error } = await supabase
          .from('locations3')
          .insert([locationData])
          .select();

        if (error) throw error;

        // Adiciona o novo local à lista
        setLocations([...locations, ...data]);
      } else {
        // Atualizar local existente
        const { error } = await supabase
          .from('locations3')
          .update(locationData)
          .eq('id', updatedLocation.id);

        if (error) throw error;

        // Atualiza a lista local com o item editado
        setLocations(locations.map(loc => 
          loc.id === updatedLocation.id ? { ...updatedLocation, ...locationData } : loc
        ));
      }

      setEditingLocation(null);
      setIsPanelOpen(false);
      
      // Recarregar os dados sem recarregar a página inteira
      await fetchLocations();
    } catch (err) {
      console.error('Erro ao salvar local:', err);
      setError(`Não foi possível ${isCreateMode ? 'criar' : 'atualizar'} o local. Por favor, tente novamente.`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="admin-panel min-h-screen bg-gray-100 p-6 overflow-y-auto">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Painel de Administração</h1>
        
        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('locations')}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === 'locations'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Map className="w-4 h-4 mr-2" />
                Locais
              </button>
              <button
                onClick={() => setActiveTab('welcome')}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === 'welcome'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Settings className="w-4 h-4 mr-2" />
                Painel de Boas-vindas
              </button>
              <button
                onClick={() => setActiveTab('galleries')}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === 'galleries'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Images className="w-4 h-4 mr-2" />
                Galerias de Imagens
              </button>
              <button
                onClick={() => setActiveTab('welcome-history')}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === 'welcome-history'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <History className="w-4 h-4 mr-2" />
                Histórico
              </button>
            </nav>
          </div>
        </div>

        {/* Conteúdo das Tabs */}
        {activeTab === 'locations' && (
          <>
            {/* Barra de ferramentas */}
            <div className="bg-white p-4 rounded-lg shadow mb-6">
              <div className="flex flex-col sm:flex-row gap-4 items-center">
                {/* Campo de busca */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Buscar por título ou descrição..."
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                
                {/* Filtro por tipo */}
                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <select
                    className="pl-10 pr-4 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                  >
                    {uniqueTypes.map((type) => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Botão Novo Local */}
                {!isPanelOpen && (
                  <button
                    onClick={handleCreateNew}
                    className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors whitespace-nowrap"
                  >
                    <PlusCircle className="w-4 h-4" />
                    Novo Local
                  </button>
                )}
              </div>
            </div>

            {/* Mensagem de erro */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
                {error}
              </div>
            )}

            {/* Painel de Edição/Criação Inline */}
            {isPanelOpen && (
              <div className="mb-6">
                <EditLocationPanel
                  location={editingLocation}
                  onClose={handleClosePanel}
                  onSave={handleSaveEdit}
                />
              </div>
            )}

            {/* Tabela de locais */}
            {!isPanelOpen && (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                {loading ? (
                  <div className="p-8 text-center text-gray-500">Carregando...</div>
                ) : filteredLocations.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    Nenhum local encontrado.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                            Título
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                            Tipo
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                            Localização
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                            Pontuação
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                            Ações
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredLocations.map((location) => {
                          const pontuacao = calcularPontuacao(location);
                          return (
                            <tr key={location.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {location.titulo}
                                </div>
                                <div className="text-xs text-gray-500 line-clamp-2">
                                  {location.descricao_detalhada?.replace(/<[^>]*>/g, '')}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  location.tipo === 'lazer' ? 'bg-blue-100 text-blue-800' :
                                  location.tipo === 'assistencia' ? 'bg-green-100 text-green-800' :
                                  location.tipo === 'historico' ? 'bg-yellow-100 text-yellow-800' :
                                  location.tipo === 'comunidades' ? 'bg-red-100 text-red-800' :
                                  location.tipo === 'educação' ? 'bg-violet-100 text-violet-800' :
                                  location.tipo === 'religiao' ? 'bg-gray-100 text-gray-800' :
                                  'bg-orange-100 text-orange-800'
                                }`}>
                                  {location.tipo}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center text-sm text-gray-500">
                                  <MapPin className="w-4 h-4 mr-1" />
                                  {location.localizacao}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden mr-2">
                                    <div 
                                      className={`h-full rounded-full transition-all duration-300 ${
                                        pontuacao >= 80 ? 'bg-green-500' :
                                        pontuacao >= 60 ? 'bg-yellow-500' :
                                        'bg-red-500'
                                      }`}
                                      style={{ width: `${pontuacao}%` }}
                                    />
                                  </div>
                                  <div className="flex items-center text-sm">
                                    <Star className="w-4 h-4 mr-1 text-yellow-400" />
                                    {pontuacao}%
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  className="text-blue-600 hover:text-blue-900 mr-3"
                                  onClick={(e) => handleEdit(location)}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  className="text-red-600 hover:text-red-900"
                                  onClick={(e) => handleDelete(location)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {activeTab === 'welcome' && (
          <WelcomePanelEditor onWelcomePanelUpdated={() => {
            // Forçar atualização do painel de boas-vindas
            window.dispatchEvent(new CustomEvent('welcomePanelUpdated'));
          }} />
        )}

        {activeTab === 'welcome-history' && (
          <WelcomePanelHistory />
        )}

        {activeTab === 'galleries' && (
          <GalleryAdminPanel />
        )}
      </div>
    </div>
  );
};

export default AdminPanel; 